import { companyMenuGroupLabels, companyScreens, moduleForCompanyScreen } from '@hue/contracts';
import {
  ArrowLeftRight,
  CalendarCheck2,
  TrendingUp,
  ArrowDownCircle,
  ArrowUpCircle,
  Handshake,
  Home,
  LineChart,
  MailCheck,
  PiggyBank,
  Receipt,
  ScrollText,
  UsersRound,
  ContactRound,
  Sparkles,
  Boxes,
  ShoppingBag,
  Percent,
  ShoppingCart,
  Package,
  Tag,
  Tags,
  CalendarClock,
  Landmark,
  ClipboardCheck,
  ListChecks,
  PackageMinus,
  PackageCheck,
  QrCode,
  WalletCards,
  FileText,
  HeartPulse,
  Store,
  Building2,
  MessageCircle,
  Palette,
  type LucideIcon,
  ReceiptText,
  Globe,
  HandCoins,
  ListTree,
  ShieldCheck,
} from 'lucide-react';

/// O endereço e o ícone de cada tela da empresa.
///
/// O rótulo e a permissão **não** estão aqui: vêm de `@hue/contracts`, o mesmo catálogo que o
/// servidor lê para decidir o que pode virar atalho. Escrever os quatro campos de novo faria a
/// tela se chamar de um jeito no menu e de outro no cartão da tela de início.
///
/// Endereço e ícone ficam do lado da web porque são desenho, e o servidor não tem o que fazer com
/// eles. Separados assim, esquecer uma tela nova **quebra na montagem** em vez de sumir com o item
/// calado.
const appearance = {
  'prepaid-withdrawals': { to: '/companies/$companyKey/prepaid-withdrawals', icon: PackageMinus },
  clients: { to: '/companies/$companyKey/clients', icon: ContactRound },
  services: { to: '/companies/$companyKey/services', icon: Sparkles },
  products: { to: '/companies/$companyKey/products', icon: Tags },
  packages: { to: '/companies/$companyKey/packages', icon: Package },
  promotions: { to: '/companies/$companyKey/promotions', icon: Tag },
  sales: { to: '/companies/$companyKey/sales', icon: ShoppingBag },
  commissions: { to: '/companies/$companyKey/commissions', icon: HandCoins },
  inventory: { to: '/companies/$companyKey/inventory', icon: Boxes },
  purchasing: { to: '/companies/$companyKey/purchasing', icon: ShoppingCart },
  protocols: { to: '/companies/$companyKey/protocols', icon: ListChecks },
  'protocol-applications': {
    to: '/companies/$companyKey/protocol-applications',
    icon: ClipboardCheck,
  },
  'material-usage': { to: '/companies/$companyKey/material-usage', icon: PackageMinus },
  'material-sufficiency': {
    to: '/companies/$companyKey/material-sufficiency',
    icon: PackageCheck,
  },
  scheduling: { to: '/companies/$companyKey/scheduling', icon: CalendarClock },
  professionals: { to: '/companies/$companyKey/professionals', icon: UsersRound },
  'payment-reports': { to: '/companies/$companyKey/payment-reports', icon: Landmark },
  finance: { to: '/companies/$companyKey/finance', icon: Landmark },
  payables: { to: '/companies/$companyKey/payables', icon: ArrowDownCircle },
  receivables: { to: '/companies/$companyKey/receivables', icon: ArrowUpCircle },
  'classification-rules': { to: '/companies/$companyKey/classification-rules', icon: ListChecks },
  classification: { to: '/companies/$companyKey/classification', icon: ListChecks },
  'chart-of-accounts': { to: '/companies/$companyKey/chart-of-accounts', icon: ListTree },
  dre: { to: '/companies/$companyKey/dre', icon: TrendingUp },
  'cash-flow': { to: '/companies/$companyKey/cash-flow', icon: ArrowLeftRight },
  'accounting-closing': { to: '/companies/$companyKey/accounting-closing', icon: CalendarCheck2 },
  partners: { to: '/companies/$companyKey/partners', icon: Handshake },
  contributions: { to: '/companies/$companyKey/contributions', icon: PiggyBank },
  entries: { to: '/companies/$companyKey/entries', icon: Receipt },
  metrics: { to: '/companies/$companyKey/metrics', icon: LineChart },
  communication: { to: '/companies/$companyKey/communication', icon: MailCheck },
  /// Dentro da engrenagem o último pedaço do endereço é a chave da tela: é assim que a visita é
  /// contada e que o menu interno sabe qual página está aberta.
  profile: { to: '/companies/$companyKey/settings/profile', icon: Building2 },
  units: { to: '/companies/$companyKey/settings/units', icon: Store },
  access: { to: '/companies/$companyKey/settings/access', icon: UsersRound },
  'record-templates': { to: '/companies/$companyKey/settings/record-templates', icon: FileText },
  'health-themes': { to: '/companies/$companyKey/settings/health-themes', icon: HeartPulse },
  'email-templates': { to: '/companies/$companyKey/settings/email-templates', icon: MailCheck },
  'whatsapp-templates': {
    to: '/companies/$companyKey/settings/whatsapp-templates',
    icon: MessageCircle,
  },
  'card-settings': { to: '/companies/$companyKey/settings/card-settings', icon: WalletCards },
  'scheduling-settings': {
    to: '/companies/$companyKey/settings/scheduling-settings',
    icon: HandCoins,
  },
  'customer-portal-settings': {
    to: '/companies/$companyKey/settings/customer-portal',
    icon: Globe,
  },
  'sales-settings': {
    to: '/companies/$companyKey/settings/sales-settings',
    icon: ReceiptText,
  },
  'commission-rules': {
    to: '/companies/$companyKey/settings/commission-rules',
    icon: Percent,
  },
  'online-payments-settings': {
    to: '/companies/$companyKey/settings/online-payments-settings',
    icon: QrCode,
  },
  'prepaid-settings': { to: '/companies/$companyKey/settings/prepaid-settings', icon: WalletCards },
  audit: { to: '/companies/$companyKey/settings/audit', icon: ScrollText },
} as const satisfies Readonly<Record<string, { to: string; icon: LucideIcon }>>;

export type CompanySectionPath =
  | '/companies/$companyKey'
  | '/companies/$companyKey/settings/appearance'
  | '/companies/$companyKey/settings/security'
  | (typeof appearance)[keyof typeof appearance]['to'];

/// Uma seção da empresa como a tela a mostra. Usada em dois lugares que precisam contar a mesma
/// história: o menu lateral e os atalhos da tela de início.
export interface CompanySection {
  key: string;
  label: string;
  to: CompanySectionPath;
  icon: LucideIcon;
  requires: readonly string[];
  moduleKey: string;
  moduleLabel: string;
  /// O grupo do menu: o assunto declarado pela tela ou, sem ele, o módulo dono (ADR 0055).
  groupKey: string;
  groupLabel: string;
  placement: 'operation' | 'settings';
}

export const companySections: readonly CompanySection[] = companyScreens.map((screen) => {
  const look = (
    appearance as Readonly<Record<string, { to: CompanySectionPath; icon: LucideIcon }>>
  )[screen.key];
  if (!look) throw new Error(`Tela "${screen.key}" sem endereço nem ícone.`);
  return {
    key: screen.key,
    label: screen.label,
    to: look.to,
    icon: look.icon,
    moduleKey: moduleForCompanyScreen(screen).key,
    moduleLabel: moduleForCompanyScreen(screen).displayName,
    groupKey: screen.menuGroup ?? moduleForCompanyScreen(screen).key,
    groupLabel: screen.menuGroup
      ? companyMenuGroupLabels[screen.menuGroup]
      : moduleForCompanyScreen(screen).displayName,
    placement: screen.placement,
    ...{ requires: screen.requires },
  };
});

/// Início não é atalho para si mesma e por isso ficou fora do catálogo — mas continua sendo o
/// primeiro item do menu, e é aqui que ela ganha corpo.
export const companyHomeSection: CompanySection = {
  key: 'home',
  label: 'Início',
  to: '/companies/$companyKey',
  icon: Home,
  requires: [],
  moduleKey: 'workspace',
  moduleLabel: 'Início',
  groupKey: 'workspace',
  groupLabel: 'Início',
  placement: 'operation',
};

/// Aparência é preferência da pessoa, não tela de módulo: não tem permissão, não entra no catálogo
/// e por isso não vira atalho nem conta visita. Toda pessoa com acesso a alcança.
export const appearanceSection: CompanySection = {
  key: 'appearance',
  label: 'Aparência',
  to: '/companies/$companyKey/settings/appearance',
  icon: Palette,
  requires: [],
  moduleKey: 'workspace',
  moduleLabel: 'Você',
  groupKey: 'workspace',
  groupLabel: 'Você',
  placement: 'settings',
};

/// Segurança também é da pessoa: trocar o próprio autenticador não depende de perfil (ADR 0081).
export const securitySection: CompanySection = {
  ...appearanceSection,
  key: 'security',
  label: 'Segurança',
  to: '/companies/$companyKey/settings/security',
  icon: ShieldCheck,
};

/// Os blocos do menu interno de Configurações, na ordem em que aparecem. O rótulo diz de quem é o
/// ajuste — da pessoa, da empresa, do Prontuário, das mensagens —, que é a pergunta de quem abre a
/// engrenagem procurando alguma coisa.
const settingsBlocks = [
  { key: 'personal', label: 'Você', screens: ['appearance', 'security'] },
  {
    key: 'company',
    label: 'Empresa',
    screens: ['profile', 'units', 'customer-portal-settings', 'access'],
  },
  { key: 'records', label: 'Prontuário', screens: ['record-templates', 'health-themes'] },
  { key: 'messages', label: 'Mensagens', screens: ['email-templates', 'whatsapp-templates'] },
  { key: 'finance', label: 'Financeiro', screens: ['card-settings'] },
  { key: 'scheduling', label: 'Agenda', screens: ['scheduling-settings'] },
  {
    key: 'sales',
    label: 'Vendas',
    screens: ['sales-settings', 'commission-rules', 'online-payments-settings'],
  },
  { key: 'prepaid', label: 'Saldos pré-pagos', screens: ['prepaid-settings'] },
  { key: 'governance', label: 'Governança', screens: ['audit'] },
] as const;

export interface SettingsBlock {
  key: string;
  label: string;
  sections: readonly CompanySection[];
}

/// Os blocos que esta pessoa alcança, sem os vazios. Aparência está sempre lá.
export function settingsBlocksVisibleTo(can: (permission: string) => boolean): SettingsBlock[] {
  const reachable = new Map(
    [appearanceSection, securitySection, ...sectionsVisibleTo(can, 'settings')].map((section) => [
      section.key,
      section,
    ]),
  );
  return settingsBlocks
    .map((block) => ({
      key: block.key,
      label: block.label,
      sections: block.screens.flatMap((key) => reachable.get(key) ?? []),
    }))
    .filter((block) => block.sections.length > 0);
}

export type CompanyNavigationItem =
  | { kind: 'section'; section: CompanySection }
  | { kind: 'group'; key: string; label: string; sections: readonly CompanySection[] };

/// Só as telas do menu operacional contam: a página de Configurações do módulo mora atrás da
/// engrenagem, e contá-la fazia Vendas virar um grupo com um único filho chamado Vendas.
const screenCountByGroup = new Map<string, number>();
for (const section of companySections) {
  if (section.placement !== 'operation') continue;
  screenCountByGroup.set(section.groupKey, (screenCountByGroup.get(section.groupKey) ?? 0) + 1);
}

/// O assunto organiza o menu (ADR 0055): o Catálogo junta o que se vende, e os demais grupos são o
/// módulo dono. Grupo de uma tela continua item direto; grupo de várias aparece mesmo quando o
/// perfil atual só alcança um dos filhos.
export function navigationItemsFor(sections: readonly CompanySection[]): CompanyNavigationItem[] {
  const items: CompanyNavigationItem[] = [];
  const grouped = new Set<string>();
  for (const section of sections) {
    if (
      section.groupKey === 'access' ||
      (screenCountByGroup.get(section.groupKey) ?? 0) <= 1 ||
      // Comissões é opcional: sem ela no perfil, Vendas segue uma entrada só, sem grupo de um filho.
      (section.groupKey === 'sales' &&
        sections.filter((candidate) => candidate.groupKey === 'sales').length <= 1)
    ) {
      items.push({ kind: 'section', section });
      continue;
    }
    if (grouped.has(section.groupKey)) continue;
    grouped.add(section.groupKey);
    items.push({
      kind: 'group',
      key: section.groupKey,
      label: section.groupLabel,
      sections: sections.filter((candidate) => candidate.groupKey === section.groupKey),
    });
  }
  return items;
}

export function findCompanySection(key: string): CompanySection | undefined {
  return companySections.find((section) => section.key === key);
}

/// Qual tela um endereço é, para poder contar a visita.
///
/// Sai do endereço, e não de uma chamada dentro de cada tela, porque assim há **um** lugar que
/// conta — oito chamadas espalhadas seriam oito chances de esquecer uma. Endereço que não é seção
/// (a própria Início, ou um endereço que não existe) responde `null`, e nada é contado.
export function screenFromPathname(pathname: string, companyKey: string): string | null {
  const prefix = `/companies/${companyKey}/`;
  if (!pathname.startsWith(prefix)) return null;
  const [first, second] = pathname.slice(prefix.length).split('/');
  const key = first === 'settings' ? second : first;
  return key && findCompanySection(key) ? key : null;
}

/// As seções que esta pessoa alcança. Repete a conta que o servidor faz — a tela não deve oferecer
/// o que a API vai recusar, e o portão da rota recusa igual.
///
/// Filtra o catálogo, onde toda tela tem porta. Início não passa por aqui: ela é a única sem
/// permissão própria, e por isso está fora do catálogo.
export function sectionsVisibleTo(
  can: (permission: string) => boolean,
  placement?: CompanySection['placement'],
): CompanySection[] {
  return companySections.filter(
    (section) =>
      (!placement || section.placement === placement) && section.requires.some((one) => can(one)),
  );
}
